import React from 'react';
import { Box, Chip, Stack, Tab, Tabs, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { formattedDate } from '../../../Utils/Functions';
import MuiDialog from '../../../SystemDesign/MuiDialog';

const ACTION_COLOR = {
  CREATE: 'success',
  READ: 'default',
  UPDATE: 'warning',
  DELETE: 'error',
  BULK_UPDATE: 'warning',
  BULK_DELETE: 'error',
  OTHER: 'info',
};

const Section = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>{title}</Typography>
    <Box>{children}</Box>
  </Box>
);

const JsonPanel = ({ data }) => (
  <Box component="pre" sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1, overflow: 'auto', maxHeight: 320 }}>
    {JSON.stringify(data ?? {}, null, 2)}
  </Box>
);

const formatVal = (v) => {
  if (v === null || v === undefined || v === '') return '–';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const DiffTable = ({ diff }) => {
  const entries = Object.entries(diff || {});
  if (!entries.length) return <Typography variant="body2">No differences</Typography>;
  return (
    <TableContainer>
      <Table size="small" stickyHeader={false}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([key, val]) => {
            const from = val?.from;
            const to = val?.to;
            return (
              <TableRow key={key} sx={{ '& td': { verticalAlign: 'top' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{key}</TableCell>
                <TableCell sx={{ color: 'error.main' }}>{formatVal(from)}</TableCell>
                <TableCell sx={{ color: 'success.main' }}>{formatVal(to)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BeforeAfterTable = ({ before, after, diff }) => {
  const beforeObj = before || {};
  const afterObj = after || {};
  const diffObj = diff || {};
  const keys = Array.from(new Set([
    ...Object.keys(beforeObj),
    ...Object.keys(afterObj),
    ...Object.keys(diffObj),
  ])).sort();

  if (!keys.length) return <Typography variant="body2">No data</Typography>;

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Before</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>After</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.map((k) => {
            const changed = Object.prototype.hasOwnProperty.call(diffObj, k);
            const beforeVal = beforeObj[k];
            const afterVal = afterObj[k];
            return (
              <TableRow key={k} sx={{ bgcolor: changed ? 'action.hover' : 'transparent' }}>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: changed ? 600 : undefined }}>{k}</TableCell>
                <TableCell sx={{ color: changed ? 'error.main' : undefined }}>{formatVal(beforeVal)}</TableCell>
                <TableCell sx={{ color: changed ? 'success.main' : undefined }}>{formatVal(afterVal)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AuditDetails = ({ open, onClose, log }) => {
  const [tab, setTab] = React.useState(0);
  const fullLog = log || null;

  const diffCount = Object.keys(fullLog?.diff || {}).length;

  const content = (
    <Box sx={{ px: 1, pt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {fullLog?.action && (
          <Chip size="small" label={fullLog.action} color={ACTION_COLOR[fullLog.action] || 'default'} />
        )}
        {typeof fullLog?.success === 'boolean' && (
          <Chip size="small" label={fullLog.success ? 'Success' : 'Failure'} color={fullLog.success ? 'success' : 'error'} />
        )}
      </Stack>

      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          ID: {fullLog?.id ?? '-'} | {fullLog?.created_at ? formattedDate(new Date(fullLog.created_at)) : '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Model: {fullLog?.model_name ?? '-'} | Entity: {fullLog?.entity_id ?? '-'} | Module: {fullLog?.module ?? '-'}
        </Typography>
        {fullLog?.user && (
          <Typography variant="body2" color="text.secondary">
            User: {typeof fullLog.user === 'object' ? (fullLog.user.username || fullLog.user.email || fullLog.user.id || '-') : String(fullLog.user)}
          </Typography>
        )}
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" sx={{ mt: 1 }}>
        <Tab label="Summary" />
        <Tab label={`Diff (${diffCount})`} />
        <Tab label="Before/After" />
        <Tab label="Raw" />
      </Tabs>

      <Box sx={{ pt: 1 }}>
        {tab === 0 && (
          <Box>
            <Section title="Route">{fullLog?.route || '-'}</Section>
            <Section title="Request ID">{fullLog?.request_id || '-'}</Section>
            <Section title="Actor User ID">{fullLog?.actor_user_id ?? '-'}</Section>
            <Section title="Error">{fullLog?.error_message || '-'}</Section>
          </Box>
        )}
        {tab === 1 && <DiffTable diff={fullLog?.diff} />}
        {tab === 2 && (
          <BeforeAfterTable before={fullLog?.before} after={fullLog?.after} diff={fullLog?.diff} />
        )}
        {tab === 3 && <JsonPanel data={fullLog ?? {}} />}
      </Box>
    </Box>
  );

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      title="Audit Details"
      maxWidth="md"
      existAction={false}
      content={content}
    />
  );
};

export default AuditDetails;
